import { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import OutletFilterSelect from "components/forms/outlet-filter-select"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const date = searchParams.get("date") || toInputDate(new Date(Date.now() - 86400000))
  // 240726 (permintaan user): grup outlet ditampilkan, tapi EOD tetap butuh SATU outlet
  // (laporan kasir harian per-cabang). Grup → outlet_id kosong → guard "pilih outlet".
  const outletValue = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const { outlet_id: resolvedOutletId } = resolveOutletFilter(outletValue)
  const cashier = searchParams.get("cashier") || ""
  const [cashierInput, setCashierInput] = useState(cashier)

  const mergeSearchParams = useCallback((nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }, [searchParams, setSearchParams])

  useEffect(() => {
    setCashierInput(cashier)
  }, [cashier])

  const submitCashier = () => {
    const nextCashier = cashierInput.trim()
    if (nextCashier === cashier) return
    mergeSearchParams({ cashier: nextCashier || null })
  }

  const handleGenerate = () => {
    if (!date) {
      toast.error("Tanggal wajib diisi sebelum generate")
      return
    }

    if (!resolvedOutletId) {
      toast.error("Pilih SATU outlet (bukan grup) untuk generate end of day")
      return
    }

    const query = new URLSearchParams({
      date,
      outlet_id: resolvedOutletId,
    })

    if (cashierInput.trim() !== "") {
      query.set("cashier", cashierInput.trim())
    }

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/orders/end-of-day-report/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Akhir Hari</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Generate laporan harian kasir dalam format Excel berdasarkan `paid_date`.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Outlet
            </label>
            <OutletFilterSelect
              value={outletValue}
              onChange={(v) => mergeSearchParams({ outlet_id: v })}
              title="Outlet"
              className="block w-full px-4 py-2 text-sm bg-white border rounded-xl border-neutral-200 focus:outline-none focus:ring focus:ring-neutral-200"
            />
          </div>

          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={date}
              onChange={(event) => mergeSearchParams({ date: event.target.value })}
            />
          </div>

          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Kasir
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Opsional"
                className="flex-1 px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
                value={cashierInput}
                onChange={(event) => setCashierInput(event.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitCashier() } }}
              />
              <button
                type="button"
                onClick={submitCashier}
                className="px-3 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
              >
                Cari
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGenerate}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Generate Excel
          </button>
        </div>
      </div>

      <div className="p-5 bg-white border border-neutral-200 rounded-2xl">
        <div className="text-sm font-semibold text-neutral-900">Keterangan</div>
        <div className="mt-3 space-y-2 text-sm text-neutral-600">
          <p>Report diambil dari transaksi yang memiliki `paid_date` pada tanggal yang dipilih.</p>
          <p>Filter kasir bersifat opsional.</p>
          <p>Output akan langsung membuka file Excel dari API CMS.</p>
        </div>
      </div>
    </div>
  )
}
